import { useEffect, useState, FunctionComponent } from 'react';
import { connect } from 'react-redux';
import styles from '../../CommonStyles/CommonGraphStyles.module.css';
import { apiCallDrilldown, apiContactsDrilldown } from '../../Services/analytics';
import { properFormat, sortAnalytics } from '../../Values/utils';
import Table from 'react-bootstrap/Table';
import './IntBar.style.css';
import { themeColors } from '../../Components/theme';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useMediaQuery, useTheme } from '@mui/material';
import "../../theme-vars.css";

type props = {
  data: any;
  type:
  | 'project'
  | 'location'
  | 'budget'
  | 'stage'
  | 'lost_reason'
  | 'call_back_reason'
  | 'not_int_reason'
  | 'propertyType'
  | 'propertyStage'
  | 'callLogs';
  heading: string;
  usersList: any;
  checked: boolean;
  teamsData: any;
  style?: any;
  show?: boolean;
  contacts: any;
  filter: 'MTD' | 'YTD' | 'PM' | 'All' | 'CUSTOM' | 'T' | 'Y';
  organizationUsers: any;
  history: any;
  user: any;
  teamLeadUsers: any;
  branchUsers: any;
  taskFilter?: any;
  leadFilter?: any;
  callFilter?: any;
  source?: boolean;
  theme: any;
  selectOrgId: any;
};

const IntBarTable: FunctionComponent<props> = ({
  data,
  heading,
  type,
  usersList,
  checked,
  teamsData,
  style,
  show,
  contacts,
  filter,
  organizationUsers,
  history,
  user,
  teamLeadUsers,
  branchUsers,
  taskFilter,
  leadFilter,
  callFilter,
  source,
  theme,
  selectOrgId
}) => {

  /** ---------------------------
 * Responsive Layout
 * ----------------------------*/
  const themeMUI = useTheme();
  const isMobile = useMediaQuery(themeMUI.breakpoints.down("sm"));
  const [tableData, setTableData] = useState<any[]>([]);
  const [header, setHeader] = useState<any[]>([]);
  const [sortData, setSort] = useState<any[]>([]);
  const [sortTable, setSortTable] = useState<boolean>(false);


  /** ---------------------------------------------------------
   * STEP 1: Build header + row data for callLogs / leadAnalytics
   * --------------------------------------------------------- */
  useEffect(() => {
    const tempHeader: string[] = ['Associate', 'Total'];
    const tempData: any[] = [];
    if (type === 'callLogs') {
      Object.keys(data).forEach((key) => {
        const analytics = data[key].callLogAnalytics;
        let sum = 0;
        Object.keys(analytics).forEach((key) => {
          if (
            !tempHeader.includes(key) &&
            key !== 'Pending' &&
            key !== 'Completed'
          ) {
            tempHeader.push(key);
          }
        });
        Object.keys(analytics).forEach((key) => {
          if (key !== 'Pending' && key !== 'Completed') {
            sum += analytics[key];
          }
        });
        tempData.push({
          name: checked ? key : source ? key : usersList[key],
          uid: key,
          Total: sum,
          ...analytics,
        });
      });
    }
    else {
      Object.keys(data).forEach((key) => {
        const analytics = data[key].leadAnalytics[type];
        let sum = 0;
        Object.keys(analytics).forEach((key) => {
          if (
            !tempHeader.includes(key) &&
            key !== 'Pending' &&
            key !== 'Completed'
          ) {
            tempHeader.push(key);
          }
        });
        Object.keys(analytics).forEach((key) => {
          if (key !== 'Pending' && key !== 'Completed') {
            sum += analytics[key];
          }
        });
        tempData.push({
          name: checked ? key : source ? key : usersList[key],
          uid: key,
          Total: sum,
          ...analytics,
        });
      });
    }

    /** ---------------------------------------------------------
     * STEP 2: If "checked", aggregate by team instead of individual
     * --------------------------------------------------------- */
    let teamTempData: any[] = [];

    if (checked) {
      tempData.forEach((user) => {
        const team = teamsData[user.name];
        let existing = teamTempData.filter((item) => item.name === team);
        if (existing.length !== 0) {
          Object.keys(user).forEach((key) => {
            if (key !== 'name') {
              if (existing[0][key]) {
                existing[0][key] += user[key];
              } else {
                existing[0][key] = user[key];
              }
            }
          });
        } else {
          teamTempData.push({ ...user, name: team });
        }
      });
    }

    /** ---------------------------------------------------------
    * STEP 3: Create Grand Total row
    * --------------------------------------------------------- */
    let gt: { [key: string]: any } = {};
    const finalData = checked ? teamTempData : source ? tempData : tempData;

    finalData.forEach((item) => {
      Object.keys(item).forEach((key) => {
        if (key !== 'name') {
          if (gt[key]) {
            gt[key] += item[key];
          } else {
            gt[key] = item[key];
          }
        }
      });
    });

    finalData.sort(sortAnalytics);
    finalData.unshift({ name: 'Grand Total', ...gt });

    // Move "Other" to end
    if (type !== 'callLogs') {
      tempHeader.forEach((item) => {
        if (item === 'Other') {
          const i = tempHeader.indexOf('Other');
          tempHeader.splice(i, 1);
          tempHeader.push('Other');
        }
      });
    }

    /** ---------------------------------------------------------
     * STEP 4: Set headers (special case for Stage)
     * --------------------------------------------------------- */
    if (type === 'stage') {
      tempHeader.forEach((item) => {
        setHeader([
          'Associate',
          'Total',
          'FRESH',
          'CALLBACK',
          'INTERESTED',
          'WON',
          'NOT INTERESTED',
          'LOST',
        ]);
      });
    } else {
      setHeader(tempHeader);
    }
    setTableData(finalData.filter((item) => item.name !== undefined));
    setSort(finalData.filter((item) => item.name !== undefined));
  }, [data, checked, usersList, source]);

  /** ---------------------------------------------------------
   * Sorting logic
   * --------------------------------------------------------- */
  const sortFunction = (item: any) => {
    setSortTable(true)
    const grandTotalObj = tableData.filter((i: any) => i.name === "Grand Total");
    let sortData: any;
    switch (item) {
      case item:
        sortData = tableData?.filter((i: any) => i.name !== "Grand Total")?.sort((a: any, b: any) => {
          const valueA = a[item] || 0;
          const valueB = b[item] || 0;
          return valueA - valueB;
        })
        break;
      default:
        break;
    }
    setSort([...grandTotalObj, ...sortData]);

  }

  /** ---------------------------------------------------------
* Sort Table Data logic
* --------------------------------------------------------- */
  const handleSortTableData = (item: any) => {
    setSortTable(false)
    const grandTotalObj = tableData.filter((i: any) => i.name === "Grand Total");
    let sortData: any;
    switch (item) {
      case item:
        sortData = tableData?.filter((i: any) => i.name !== "Grand Total")?.sort((a: any, b: any) => {
          const valueA = a[item] || 0;
          const valueB = b[item] || 0;
          return valueB - valueA;
        })
        break;
      default:
        break;
    }
    setSort([...grandTotalObj, ...sortData]);
  }

  useEffect(() => {
    setTableData(sortData);
  }, [sortData])

  /** ------------------------------------------------
* Reusable styling for tables/cards by theme
* ------------------------------------------------ */
  const tableTextStyles = { color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark }


  /** CSS Variables */
  const fontFamilyMain = "var(--font-family-main)";
  const fontSizeSmall = "var(--font-size-small)";

  /** --------------------------------------------------------
   * RENDER
   * -------------------------------------------------------- */
  return (
    <>
      <div
        className={styles.detailsBox}
        style={{
          ...style,
          overflowX: "auto",
          padding: isMobile ? "0.5rem" : 0,
          minHeight: 300
        }}
      >
        <Table
          striped
          bordered
          hover
          style={{
            minWidth: 600,
            tableLayout: "auto",
            fontFamily: fontFamilyMain,
            fontSize: fontSizeSmall,
          }}
        >
          <thead>
            <tr>
              <th
                style={{
                  width: "auto",
                  textAlign: "center",
                  ...tableTextStyles,
                  whiteSpace: "nowrap",
                  fontFamily: fontFamilyMain,
                  fontSize: fontSizeSmall
                }}
              >
                S.No
              </th>

              {/* Dynamic Headers */}
              {header.map((item, index) => (
                <th
                  key={index}
                  style={{
                    textAlign: item === 'Associate' ? 'left' : 'center',
                    ...tableTextStyles,
                    whiteSpace: "nowrap",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                    {properFormat(item)}
                    {item !== "Associate" && (
                      sortTable
                        ? <ExpandLessIcon style={{ cursor: "pointer" }} onClick={() => handleSortTableData(item)} />
                        : <KeyboardArrowDownIcon style={{ cursor: "pointer" }} onClick={() => sortFunction(item)} />
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>

            {/* Dynamic Data Render */}
            {tableData.map((item, index) => (
              <tr key={index} style={{ backgroundColor: index === 0 ? "rgb(233, 236, 239)" : '', fontSize: fontSizeSmall, fontFamily: fontFamilyMain }} >
                <th style={{
                  width: "auto",
                  textAlign: "center",
                  ...tableTextStyles,
                  whiteSpace: "nowrap"
                }}>
                  {index > 0 ? <>{index}</> : null}
                </th>
                <th
                  style={{
                    fontWeight: 'bold',
                    whiteSpace: "nowrap",
                    textAlign: 'center'
                  }}
                >
                  {properFormat(item.name)}
                </th>
                {header.slice(1).map((key: string, sIndex) => (
                  <th
                    key={String(index) + String(sIndex)}
                    style={{
                      cursor: item[key] ? 'pointer' : 'not-allowed',
                      textAlign: 'center',
                      fontWeight: index === 0 ? "bold" : 'normal',
                      whiteSpace: "nowrap"
                    }}
                  >
                    {item[key] ? properFormat(item[key]) : 0}
                  </th>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </>
  );
};

/**
 * Maps Redux state to component props.
 */
const mapStateToProps = (state: any) => {
  return {
    contacts: state.contacts,
    organizationUsers: state.organizationUsers.data,
    user: state.user.data,
    teamLeadUsers: state.teamLeadUsers.data,
    branchUsers: state.branchUsers.data,
    theme: state.theme.isLightMode,
    selectOrgId: state.selectOrganizationId.data
  };
};
export default connect(mapStateToProps)(IntBarTable);
